import { useRef, useState, useEffect, useCallback } from 'react';

import { useTheme } from '@mui/material';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import useNavigate from 'src/hooks/use-navigate';

import { useUserStore } from 'src/stores/user/user.store';
import { useAnalyticsContext } from 'src/providers/analytics-provider';
import { benefitsPathDict, benefitsPathsArray } from 'src/_mock/_analytics';
import { useCollectiblesStore } from 'src/stores/benefits/collectibles.store';

export default function useDefaultLayout() {
  const [value, setValue] = useState(-1);
  const bottomNavRef = useRef<HTMLDivElement | null>(null);
  const [bottomNavHeight, setBottomNavHeight] = useState(0);
  const theme = useTheme();
  const pathname = usePathname();
  const [isBenefitsBottomsheetOpen, setIsBenefitsBottomsheetOpen] = useUserStore(
    (state) => [
      state.isBenefitsBottomsheetOpen,
      state.setIsBenefitsBottomsheetOpen
    ]
  );
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(false);
  const [isAccumulatePoints, setIsAccumulatePoints] = useState(false);
  const [sliderOpen, setSliderOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState<any>(false);
  const [selectedTab, setSelectedTab] = useState('benefits');
  const navigate = useNavigate();
  const { logAction } = useAnalyticsContext();
  const { setOpenSendNotes } = useUserStore();
  const { hasActiveCollection } = useCollectiblesStore();
  const { user } = useUserStore();
  const invoiceRef = useRef(null);
  const TABS = [
    { title: 'Benefícios', tab: 'benefits' },
    { title: 'Meu Viva', tab: 'my-viva' },
  ];

  const toggleBenefitsMenu = () => {
    setIsBenefitsOpen((prev) => !prev);
  };

  const toggleAccumulatePointsMenu = () => {
    setIsAccumulatePoints((prev) => !prev);
  };

  const onClickItem = (goTo: string) => {
    setSliderOpen(false);
    document.body.style.overflow = 'auto';
    if (benefitsPathsArray.includes(goTo)) {
      logAction('benefitsNav', benefitsPathDict[goTo]);
    }
    setTimeout(() => {
      if (goTo === 'sendNote') {
        setOpenSendNotes(true, logAction);
      } else {
        navigate(goTo);
      }
    }, 100);
  };

  const menuItems = [
    { label: 'ENVIAR NOTAS', hasChevron: false, path: 'sendNote', onClickLabel: onClickItem },
    { label: 'EXPERIÊNCIAS', hasChevron: false, path: paths.events.root, onClickLabel: onClickItem },
    {
      label: 'SORTEIOS',
      hasChevron: false,
      path: paths.promotions.root,
      onClickLabel: onClickItem,
    },
    {
      label: 'BENEFÍCIOS',
      hasChevron: true,
      isOpen: isBenefitsOpen,
      onClick: toggleBenefitsMenu,
      onClickSubItem: onClickItem,
      subItems: [
        ...(hasActiveCollection
          ? [
            {
              label: 'Colecionáveis',
              path: paths.collectibles.root,
            },
          ]
          : []),
        // {
        //   label: 'Experiências',
        //   path: paths.experiences.root,
        // },
        {
          label: 'Produtos',
          path: paths.products.root,
        },
        {
          label: 'Sorteios',
          path: paths.promotions.root,
        },
        {
          label: 'Cupons',
          path: paths.coupons.root,
        },
      ],
    },
    {
      label: 'ACUMULAR PONTOS',
      hasChevron: true,
      isOpen: isAccumulatePoints,
      onClick: toggleAccumulatePointsMenu,
      onClickSubItem: onClickItem,
      subItems: [
        {
          label: 'Enviar notas',
          path: 'sendNote',
        },
        {
          label: 'Missões',
          path: paths.missionsList.root,
        },
        {
          label: 'Check-in',
          path: paths.missionsList.root,
        },
        // {
        //   label: 'Jogos',
        //   path: paths.home.root,
        // },
      ],
      breakLine: true,
    },
  ];

  const handleChangeTab = useCallback((tab: string) => {
    setSelectedTab(tab);
  }, []);

  useEffect(() => {
    if (bottomNavRef.current) {
      setBottomNavHeight(bottomNavRef.current.clientHeight);
    }
  }, [bottomNavRef.current]);

  const onClickBottomNavigation = async (newValue: number) => {
    if (newValue === 4) {
      navigate(paths.navigationMenu.root);
    } else if (newValue === 3) {
      navigate(paths.benefits.root);
    } else if (newValue === 0) {
      logAction('homeNavBar', pathname);
      navigate(paths.home.root);
    } else if (newValue === 1) {
      logAction('searchNav');
      navigate(paths.search.root);
    } else if (newValue === 2) {
      setOpenSendNotes(true, logAction);
    }
  };

  const sliderMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let timer: any;
    if (pathname.includes('/home') && user?.tooltipParams?.idleTimeToShow) {
      timer = setTimeout(() => {
        setShowTooltip(invoiceRef.current);
      }, user.tooltipParams.idleTimeToShow * 1000);
    } else {
      setShowTooltip(false);
    }
    return () => clearTimeout(timer);
  }, [pathname, user, invoiceRef]);

  useEffect(() => {
    let timer: any;
    if (showTooltip && user?.tooltipParams?.timeToHide) {
      timer = setTimeout(() => {
        setShowTooltip(false);
      }, user.tooltipParams.timeToHide * 1000);
    }
    return () => clearTimeout(timer);
  }, [showTooltip, user]);

  return {
    bottomNavHeight,
    bottomNavRef,
    sliderOpen,
    theme,
    value,
    pathname,
    TABS,
    selectedTab,
    isBenefitsOpen,
    isAccumulatePoints,
    menuItems,
    sliderMenuRef,
    isBenefitsBottomsheetOpen,
    showTooltip,
    invoiceRef,
    setIsBenefitsBottomsheetOpen,
    onClickBottomNavigation,
    setSliderOpen,
    toggleAccumulatePointsMenu,
    setValue,
    handleChangeTab,
    toggleBenefitsMenu,
  };
}
